import React from 'react';
import { CheckCircle, FileText, Shield, Award, Clock, TrendingUp } from 'lucide-react';
import ServiceFAQ from '../../components/ServiceFAQ';

const BusinessDisputes = ({ onGetStarted }) => {
  const benefits = ['Commercial litigation representation', 'Alternative dispute resolution', 'Breach of contract claims', 'Business tort litigation', 'Shareholder and partnership disputes', 'Preliminary injunctions and emergency relief'];
  const process = [
    { title: 'Case Assessment', description: 'Evaluate merits, strategy, and potential outcomes.' },
    { title: 'Strategic Planning', description: 'Develop litigation or settlement strategy.' },
    { title: 'Discovery & Motion Practice', description: 'Conduct discovery and file necessary motions.' },
    { title: 'Resolution', description: 'Achieve favorable outcome through trial or settlement.' },
  ];
  const disputeTypes = ['Breach of Contract', 'Business Torts', 'Shareholder Disputes', 'Partnership Dissolution', 'Trade Secret Misappropriation', 'Fraud Claims', 'Fiduciary Duty Breaches', 'Commercial Real Estate'];
  const whyChoose = [
    { icon: Award, title: 'Litigation Excellence', description: 'Proven success in complex business litigation.' },
    { icon: Shield, title: 'Strategic Defense', description: 'Aggressive protection of your business interests.' },
    { icon: Clock, title: 'Efficient Resolution', description: 'Cost-effective strategies to resolve disputes quickly.' },
    { icon: TrendingUp, title: 'Results Focused', description: 'Dedicated to achieving favorable outcomes.' },
  ];
  const faqs = [
    { question: 'When should I hire a business litigation attorney?', answer: 'Hire an attorney as soon as a dispute arises or you receive a demand letter. Early involvement allows us to assess the situation, preserve evidence, explore settlement options, and develop litigation strategy if necessary.' },
    { question: 'What is alternative dispute resolution?', answer: 'Alternative Dispute Resolution (ADR) includes mediation and arbitration. These processes can resolve disputes faster and less expensively than litigation. Mediation involves a neutral facilitator, while arbitration results in a binding decision.' },
    { question: 'How long does business litigation take?', answer: 'Timeline varies greatly depending on case complexity, court schedule, and whether parties settle. Simple cases may resolve in months, while complex litigation can take years. We provide realistic timeline estimates based on your specific situation.' },
    { question: 'What are the costs of business litigation?', answer: 'Costs depend on case complexity, discovery needs, and duration. We offer various fee structures including hourly rates, fixed fees for certain matters, and contingency arrangements when appropriate. We provide transparent cost estimates upfront.' },
    { question: 'Should we try to settle or go to trial?', answer: 'Settlement decisions depend on many factors including strength of case, costs, business disruption, and desired outcome. We analyze settlement offers against trial prospects and provide strategic recommendations aligned with your business objectives.' },
  ];

  return (
    <div className="pt-20">
      <section className="py-24 md:py-32 relative overflow-hidden" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1758541213979-fe8c9996e197?w=1920&q=80)', backgroundSize: 'cover', backgroundPosition: 'center' }}>
        <div className="absolute inset-0 bg-[#2C3E50]/80"></div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="max-w-3xl">
            <div className="text-sm uppercase tracking-[0.2em] font-medium text-white/90 mb-4">Compliance & Protection Services</div>
            <h1 className="text-5xl lg:text-6xl tracking-tight leading-none text-white font-bold mb-6">Business Disputes</h1>
            <p className="text-xl text-white/90 leading-relaxed mb-8">Litigation and dispute resolution for commercial matters. Aggressive representation to protect your business interests.</p>
            <button onClick={onGetStarted} className="bg-[#CA9A67] text-white hover:bg-[#b8894f] transition-colors px-8 py-4 font-medium">Get Started</button>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl lg:text-4xl tracking-tight text-[#2E2E2E] font-bold mb-6">Overview</h2>
              <p className="text-base leading-relaxed text-[#2E2E2E]/80 mb-6">Business disputes can threaten operations and profitability. Our litigation team provides aggressive representation in commercial disputes including breach of contract, business torts, and shareholder conflicts.</p>
              <p className="text-base leading-relaxed text-[#2E2E2E]/80">We pursue favorable outcomes through litigation or alternative dispute resolution.</p>
              <h3 className="text-xl lg:text-2xl text-[#2C3E50] font-semibold mb-6 mt-8">Key Benefits</h3>
              <ul className="space-y-4">
                {benefits.map((benefit, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-[#2C3E50] mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                    <span className="text-base text-[#2E2E2E]/80">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <img src="https://images.unsplash.com/photo-1767972463877-b64ba4283cd0?w=800&q=80" alt="Legal dispute resolution" className="w-full h-[450px] object-cover shadow-lg" />
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl tracking-tight text-[#2E2E2E] font-bold mb-4">Types of Business Disputes</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mobile-swiper">
            {disputeTypes.map((type, index) => (
              <div key={index} className="bg-white border border-[#2C3E50]/5 shadow-sm p-6">
                <FileText className="w-8 h-8 text-[#CA9A67] mb-3" strokeWidth={1.5} />
                <h4 className="font-semibold text-[#2C3E50]">{type}</h4>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 relative overflow-hidden" style={{ backgroundImage: 'url(https://images.pexels.com/photos/5669602/pexels-photo-5669602.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=750&w=1260)', backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: 'fixed' }}>
        <div className="absolute inset-0 bg-[#2C3E50]/90"></div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="text-center mb-16">
            <div className="text-sm uppercase tracking-[0.2em] font-medium text-[#CA9A67] mb-4">Our Approach</div>
            <h2 className="text-3xl lg:text-4xl tracking-tight text-white font-bold mb-4">Our Process</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mobile-swiper">
            {process.map((step, index) => (
              <div key={index} className="bg-white/5 backdrop-blur-sm border border-white/10 p-6 hover:bg-white/10 transition-all duration-300">
                <div className="text-4xl font-bold text-[#CA9A67] mb-4">{index + 1}</div>
                <h3 className="text-xl text-white font-semibold mb-3">{step.title}</h3>
                <p className="text-sm text-white/70 leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <img src="https://images.unsplash.com/photo-1769029265788-d7921a103403?w=800&q=80" alt="Why choose our dispute resolution team" className="w-full h-[500px] object-cover shadow-lg" />
            </div>
            <div>
              <h2 className="text-3xl lg:text-4xl tracking-tight text-[#2E2E2E] font-bold mb-8">Why Choose Us</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mobile-swiper">
                {whyChoose.map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <div key={index} className="bg-white border border-[#2C3E50]/5 shadow-sm p-6">
                      <div className="inline-flex items-center justify-center w-12 h-12 bg-[#CA9A67] mb-4">
                        <Icon className="w-6 h-6 text-white" strokeWidth={1.5} />
                      </div>
                      <h3 className="text-lg text-[#2C3E50] font-semibold mb-2">{item.title}</h3>
                      <p className="text-sm text-[#2E2E2E]/70 leading-relaxed">{item.description}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      <ServiceFAQ faqs={faqs} />

      <section className="py-24 md:py-32 relative overflow-hidden" style={{ backgroundImage: 'url(https://images.pexels.com/photos/5669602/pexels-photo-5669602.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)', backgroundSize: 'cover', backgroundPosition: 'center' }}>
        <div className="absolute inset-0 bg-[#2C3E50]/85"></div>
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center relative z-10">
          <h2 className="text-3xl lg:text-4xl tracking-tight text-white font-bold mb-6">Facing a Business Dispute?</h2>
          <p className="text-xl text-white/90 leading-relaxed mb-8">Contact us for aggressive litigation representation.</p>
          <button onClick={onGetStarted} className="bg-[#CA9A67] text-white hover:bg-[#b8894f] transition-colors px-8 py-4 font-medium">Get Legal Help Now</button>
        </div>
      </section>
    </div>
  );
};

export default BusinessDisputes;
